/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/useClaimDocumentActions.ts
 * FILE_ID: QUOTE-CLAIMS-G10-ACTIONS-HOOK-001
 * PURPOSE: Own claim artifact downloads and idempotent client sends.
 */
import { useCallback, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveBlobDownload } from '@quotech/api'
import { useToast } from '../../../components/builder/Toast'
import {
  createOrReuseDocumentSendAttempt,
  type DocumentSendAttempt,
  type DocumentSendPayload,
} from './documentSendPayload'
import type { DocumentSendRequest } from './RecipientPickerDialog'
import type { ClaimEditorController } from './useClaimEditorController'

function message(error: unknown) {
  return (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
    ?? (error instanceof Error ? error.message : 'Request failed')
}

export function useClaimDocumentActions(controller: ClaimEditorController) {
  const claim = controller.claim
  const queryClient = useQueryClient()
  const toast = useToast()
  const [recipientOpen, setRecipientOpen] = useState(false)
  const sendAttempt = useRef<DocumentSendAttempt | null>(null)
  const send = useMutation({
    mutationFn: async (payload: DocumentSendPayload) =>
      (await controller.api.post(`/api/claims/${claim?.id}/send_to_client/`, payload)).data,
    onSuccess: async () => {
      sendAttempt.current = null
      setRecipientOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['claim', claim?.id] })
    },
    onError: (error) => toast.error('Failed to send document', message(error)),
  })
  const download = useCallback(async (format: 'pdf' | 'docx' | 'xlsx') => {
    if (!claim) return
    try {
      const endpoint = format === 'pdf'
        ? 'export_pdf'
        : format === 'docx' ? 'generate_docx' : 'export-xlsx'
      const response = await controller.api.get(`/api/claims/${claim.id}/${endpoint}/`, { responseType: 'blob' })
      saveBlobDownload({
        blob: response.data as Blob,
        fallbackFilename: `${claim.doc_type ?? 'claim'}-${(claim as { contract_sequence?: number | null }).contract_sequence ?? claim.claim_number}.${format}`,
      })
    } catch (error) {
      toast.error(`Failed to download ${format.toUpperCase()}`, message(error))
    }
  }, [claim, controller.api, toast])
  const submitSend = useCallback((request: DocumentSendRequest) => {
    if (!claim) return
    const attempt = createOrReuseDocumentSendAttempt(claim.id, request, sendAttempt.current)
    sendAttempt.current = attempt
    send.mutate(attempt.payload)
  }, [claim, send])
  const openRecipient = useCallback(() => {
    sendAttempt.current = null
    setRecipientOpen(true)
  }, [])
  const closeRecipient = useCallback(() => {
    sendAttempt.current = null
    setRecipientOpen(false)
  }, [])
  return {
    recipientOpen,
    openRecipient,
    closeRecipient,
    sendToClient: submitSend,
    sending: send.isPending,
    download,
  }
}
